// TODO: core methods for using the main view, for letting us re-use functionality

import type { Raw } from "vue"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { PropertyId } from "~/classes/entity"
import { ReactiveFile } from "~/classes/file/ReactiveFile"
import { StaticFile } from "~/classes/file/StaticFile"
import { ViewModeType } from "~/classes/generated/interface"
import { StaticSearch } from "~/classes/search/StaticSearch"
import { maxElement } from "~/utils/helpers"

interface State {
  state: InterfaceCollectionModeType
  /**
   * Files indexed by their file id. This will be a problem once we show search results
   * of file snippets (multiple snippets per file), but we can cross that bridge later
   * NOTE: These MUST be `markRaw(file)`, since we don't want reactivity leaking into
   * this deeply nested structure
   */
  contents: Record<string, Raw<StaticFile>>
  contentBounds: Record<string, DOMRect>
  /**
   * Used to track folders created from the current window/tab.
   */
  createdFolders: Record<string, boolean>
}

class CollectionView extends GlobalComposable<State> {
  override defaultState = (): State => {
    return { state: "folder", contents: {}, contentBounds: {}, createdFolders: {} }
  }

  override onCreate = () => {
    const { addInterfaceEventListener } = useEvents()
    addInterfaceEventListener("afterNavigatedToFolder", this.onAccessedFolder)
    addInterfaceEventListener("afterNavigatedToSearch", this.onExecutedSearch)
    addInterfaceEventListener("afterExpandedFolder", this.onExpandedFolder)
    addInterfaceEventListener("afterShrunkFolder", this.onShrunkFolder)
    addInterfaceEventListener("wentOnlineAuthStatus", this.onWentOnline)
    addInterfaceEventListener("wentOnlineAnonStatus", this.onWentOnline)
  }

  override onDestroy = () => {
    const { removeInterfaceEventListener } = useEvents()
    removeInterfaceEventListener("afterNavigatedToFolder", this.onAccessedFolder)
    removeInterfaceEventListener("afterNavigatedToSearch", this.onExecutedSearch)
    removeInterfaceEventListener("wentOnlineAuthStatus", this.onWentOnline)
    removeInterfaceEventListener("wentOnlineAnonStatus", this.onWentOnline)
    removeInterfaceEventListener("afterExpandedFolder", this.onExpandedFolder)
    removeInterfaceEventListener("afterShrunkFolder", this.onShrunkFolder)
  }

  clearCollectionViewState = () => {
    this.state.value = this.defaultState()
  }

  isInFolderMode = () => {
    return this.state.value.state === "folder"
  }

  isInAutocompleteMode = () => {
    return this.state.value.state === "autocomplete"
  }

  isInSearchMode = () => {
    return this.state.value.state === "search-results"
  }

  switchCollectionType = (type: InterfaceCollectionModeType) => {
    this.clearViewModeListeners()
    this.state.value.state = type
  }

  /**
   * Changes view mode and updates any relevant state in the collection
   * view.
   */
  switchCollectionViewMode = (mode: InterfaceViewModeType) => {
    const { capture } = useAnalytics()
    const { switchViewMode, state: viewModeState } = useViewMode()
    const { currentRootFolder, currentSearch } = useNavigation()

    if (viewModeState.value.current.mode === mode)
      return console.warn("Already in this view mode")

    // content bounds need to be reset
    this.state.value.contentBounds = {}

    this.clearViewModeListeners()

    switchViewMode(mode)

    if (this.state.value.state === "folder") {
      if (!isDefined(currentRootFolder.value))
        return console.error("Cannot switch view mode without a folder")
      this.onNavigatedToFolder(currentRootFolder.value!.asStatic())
    }
    else if (this.state.value.state === "search-results") {
      if (!isDefined(currentSearch.value))
        return console.error("Cannot switch view mode without a search")
      this.onNavigatedToSearch(currentSearch.value!.asStatic())
    }
    else if (this.state.value.state === "autocomplete") {
      console.log("Switching to autocomplete mode, callbacks not implemented yet")
    }
    else
      throw new Error("Invalid state during view mode switch")
    // TODO: if leaving column mode, we can clean up folders that aren't the current one
    // e.g. parents and children
    capture("user_changed_view_mode", { mode })
  }

  /**
   * Sorts properties in place by the current view mode sort. This works by
   * iterating over the different sorts and if the sort is zero, moves onto the next
   * sorting comparator.
   */
  sortByViewModeSort = (files: StaticFile[]) => {
    const { state: viewModeState, isCustomSort } = useViewMode()
    const { state: instantSearchState } = useInstantSearch()
    const { isGrouping, DEFAULT_SORT_DESCENDING, DEFAULT_SORT_PROPERTY } = useViewMode()
    const { currentSearch } = useNavigation()

    const grouped = isGrouping()

    if (this.isInSearchMode() && !isCustomSort()) {
    // For annoying reasons, we can't actually use `currentSearchResults` here as it
    // is a computed property that hasn't necessarily been updated yet.
      const currentSearchResults = currentSearch.value?.searchResults() ?? {}
      const resultsByFile = groupedDict(Object.values(currentSearchResults), "fileId")
      return files.sort((a, b) => this._sortBySearchRelevance(a, b, true, resultsByFile))
    }

    if (this.isInAutocompleteMode()) {
      const searchResults = []
      if (instantSearchState.value.autocompleteText.response?.search) {
        const textSearch = new StaticSearch(instantSearchState.value.autocompleteText.response.search)
        searchResults.push(...Object.values(textSearch.searchResults()))
      }
      if (instantSearchState.value.autocompleteSmart.response?.search) {
        const smartSearch = new StaticSearch(instantSearchState.value.autocompleteSmart.response.search)
        searchResults.push(...Object.values(smartSearch.searchResults()))
      }
      const resultsByFile = groupedDict(searchResults, "fileId")
      return files.sort((a, b) => this._sortBySearchRelevance(a, b, true, resultsByFile))
    }

    // The 'default' sort
    if (!isCustomSort())
      return files.sort((a, b) => StaticFile.compare(a, b, DEFAULT_SORT_PROPERTY, DEFAULT_SORT_DESCENDING, grouped))

    return files.sort((a, b) => {
      for (const sort of viewModeState.value.current.sort) {
        const diff = StaticFile.compare(a, b, sort.propertyId, sort.descending, grouped)
        if (diff !== 0)
          return diff
      }
      return 0
    })
  }

  _sortBySearchRelevance = (
    a: StaticFile,
    b: StaticFile,
    descending: boolean,
    resultsByFile: Record<string, SearchResult[]>,
  ) => {
    if (!isDefined(resultsByFile[a.id]) || !isDefined(resultsByFile[b.id]))
      return 0
    const aRelevance = maxElement(resultsByFile[a.id], r => r.score)?.score ?? 0
    const bRelevance = maxElement(resultsByFile[b.id], r => r.score)?.score ?? 0
    const diff = aRelevance - bRelevance
    return descending ? -diff : diff
  }

  clearViewModeListeners = () => {
    const currentViewMode = useViewMode().state.value.current.mode
    if (currentViewMode === ViewModeType.Icon)
      return useIconView().clearStateAndListeners()
    else if (currentViewMode === ViewModeType.Grid)
      return useGridView().clearStateAndListeners()
    else if (currentViewMode === ViewModeType.Feed)
      return useFeedView().clearStateAndListeners()
    else if (currentViewMode === ViewModeType.Detail)
      return useDetailView().clearStateAndListeners()
    else if (currentViewMode === ViewModeType.Table)
      return useTableView().clearStateAndListeners()
    else if (currentViewMode === ViewModeType.Tree)
      return useTreeView().clearStateAndListeners()
    else if (currentViewMode === ViewModeType.Column)
      return useColumnView().clearStateAndListeners()
    else if (currentViewMode === ViewModeType.Gallery)
      return useGalleryView().clearStateAndListeners()
    throw new Error("View mode type not supported")
  }

  /**
   * NOTE: There is an 'unknown' group with an empty id that we need to account for,
   * which represents files that don't have the property or who don't satisfy any of
   * the conditions.
   * NOTE: The ordering of groups here is determined by the order of the files
   * given as input. If the given files are out of order then the groups will be out
   * of order as well.
   */
  groupByViewModeGroup = (files: StaticFile[]): {
    id: string
    files: StaticFile[]
  }[] => {
    const { state: viewModeState, groupableProperties } = useViewMode()
    const { state: instantSearchState } = useInstantSearch()

    const groups: {
      id: string
      files: StaticFile[]
    }[] = []

    // If we're in autocomplete mode, we have two parallel searches -- the text and the
    // smart search.
    // TODO: Make the set of searches dynamic and move the method into `useInstantSearch`
    if (this.isInAutocompleteMode()) {
      if (instantSearchState.value.autocompleteText.response?.files) {
        // files are pre-sorted
        const files = instantSearchState.value.autocompleteText.response.files.map(f => new StaticFile(f))
        groups.push({ id: "Text Results", files })
      }
      if (instantSearchState.value.autocompleteSmart.response?.files) {
        // files are pre-sorted
        const files = instantSearchState.value.autocompleteSmart.response.files.map(f => new StaticFile(f))
        groups.push({ id: "Smart Results", files })
      }
      return groups
    }

    const group = viewModeState.value.current.group
    if (!isDefined(group))
      return [] // don't error, we might be in a state change
    const definition = groupableProperties[group.propertyId]
    if (!isDefined(definition)) {
      console.error(`Group definition not found for property ${group.propertyId}`)
      return []
    }

    for (const file of files) {
      const id = definition.classify(file)
      const grp = groups.find(g => g.id === id)
      if (isDefined(grp))
        grp.files.push(file)
      else
        groups.push({ id, files: [file] })
    }

    return groups
  }

  /**
   * When you navigated to a folder and want to explicitly set the accessed at
   * timestamp
   */
  onAccessedFolder = (folder: StaticFile) => {
    folder.setFileAccessedIfAllowed()

    this.clearViewModeListeners()
    return this.onNavigatedToFolder(folder)
  }

  onNavigatedToFolder = (folder: StaticFile) => {
    const currentViewMode = useViewMode().state.value.current.mode
    if (currentViewMode === ViewModeType.Icon)
      return useIconView().onNavigatedToFolder(folder)
    else if (currentViewMode === ViewModeType.Grid)
      return useGridView().onNavigatedToFolder(folder)
    else if (currentViewMode === ViewModeType.Feed)
      return useFeedView().onNavigatedToFolder(folder)
    else if (currentViewMode === ViewModeType.Detail)
      return useDetailView().onNavigatedToFolder(folder)
    else if (currentViewMode === ViewModeType.Table)
      return useTableView().onNavigatedToFolder(folder)
    else if (currentViewMode === ViewModeType.Tree)
      return useTreeView().onNavigatedToFolder(folder)
    else if (currentViewMode === ViewModeType.Column)
      return useColumnView().onNavigatedToFolder(folder)
    else if (currentViewMode === ViewModeType.Gallery)
      return useGalleryView().onNavigatedToFolder(folder)
    throw new Error("View mode type not supported")
  }

  storeCreatedFolder = (folderId: string) => {
    this.state.value.createdFolders[folderId] = true
  }

  /**
   * Currently just a wrapper over `onNavigatedToSearch` but we might want to
   * do some additional actions in the future.
   */
  onExecutedSearch = (search: StaticSearch) => {
    this.clearViewModeListeners()
    return this.onNavigatedToSearch(search)
  }

  onNavigatedToSearch = (search: StaticSearch) => {
    const { switchViewMode } = useViewMode()

    console.warn("onNavigatedToSearch not fully implemented")
    const currentViewMode = useViewMode().state.value.current.mode
    if (currentViewMode === ViewModeType.Icon)
      return useIconView().onNavigatedToSearch(search)
    else if (currentViewMode === ViewModeType.Grid)
      return useGridView().onNavigatedToSearch(search)
    else if (currentViewMode === ViewModeType.Feed)
      return useFeedView().onNavigatedToSearch(search)
    else if (currentViewMode === ViewModeType.Detail)
      return useDetailView().onNavigatedToSearch(search)
    else if (currentViewMode === ViewModeType.Table)
      return useTableView().onNavigatedToSearch(search)

    // TODO: allow for searches in tree and column view
    // else if (currentViewMode === ViewModeType.Tree)
    //   return useTreeView().onNavigatedToSearch(search)
    // else if (currentViewMode === ViewModeType.Column)
    //   return useColumnView().onNavigatedToSearch(search)

    console.warn("Searches in column and tree view are not yet implemented, falling back to icon view")
    switchViewMode(ViewModeType.Icon)
    return useIconView().onNavigatedToSearch(search)
  }

  onReceivedAutocompleteResults = (results: StaticFile[]) => {
    const { state: viewModeState } = useViewMode()

    if (!this.isInAutocompleteMode())
      this.switchCollectionType("autocomplete")

    if (viewModeState.value.current.mode !== ViewModeType.Icon)
      this.switchCollectionViewMode(ViewModeType.Icon) // TODO: other view modes for search

    // mark all the results as raw
    this.state.value.contents = dictByKey(
      results.map(f => markRaw(f)),
      file => file.id,
    )
    this.updateFileContentBoundsSync()
  }

  onWentOnline = () => {
    if (this.state.value.state === "search-results")
      return console.warn("Going online hydration not implemented for search-results yet")

    if (this.state.value.state !== "folder")
      return // don't need to address the handler
    const currentViewMode = useViewMode().state.value.current.mode
    if (currentViewMode === ViewModeType.Icon)
      return useIconView().onWentOnline()
    else if (currentViewMode === ViewModeType.Grid)
      return useGridView().onWentOnline()
    else if (currentViewMode === ViewModeType.Feed)
      return useFeedView().onWentOnline()
    else if (currentViewMode === ViewModeType.Detail)
      return useDetailView().onWentOnline()
    else if (currentViewMode === ViewModeType.Table)
      return useTableView().onWentOnline()
    else if (currentViewMode === ViewModeType.Tree)
      return useTreeView().onWentOnline()
    else if (currentViewMode === ViewModeType.Column)
      return useColumnView().onWentOnline()
    else if (currentViewMode === ViewModeType.Gallery)
      return useGalleryView().onWentOnline()
    throw new Error("Selecting all not supported in this mode")
  }

  /**
   * gets all the files in the current selection
   */
  getAllFilesInCollection = (): Readonly<Record<string, StaticFile>> => {
    return this.state.value.contents
  }

  /**
   * Gets a static file from our current collection, the files that are visible in the
   * current view.
   * @returns a file without reactivity
   */
  getStaticFile = (fileId?: string): StaticFile | undefined => {
    return fileId ? this.state.value.contents[fileId] : undefined
  }

  /**
   * Gets a reactive ref to a file stored in our current collection. Changes made to this file
   * will be reflected in all watchers of this file, and other reactive files created from this
   * @returns a reactive file
   */
  getReactiveFile = (fileId: string, location: SelectionLocation): ReactiveFile | undefined => {
    const staticFile = this.getStaticFile(fileId)
    if (!isDefined(staticFile))
      return
    const file = computed({
      get: () => this.getStaticFile(fileId)!,
      set: (newFile) => {
        this.state.value.contents[fileId] = markRaw(newFile)
      },
    })
    return new ReactiveFile({ file, location })
  }

  onExpandedFolder = (folder: StaticFile) => {
    const { state: viewModeState } = useViewMode()

    const currentViewMode = viewModeState.value.current.mode
    if (currentViewMode === ViewModeType.Tree)
      return useTreeView().onExpandedFolder(folder)
    else if (currentViewMode === ViewModeType.Column)
      return useColumnView().onExpandedFolder(folder)
    throw new Error("Expanding folder supported in this mode")
  }

  onShrunkFolder = (folder: StaticFile) => {
    const { state: viewModeState } = useViewMode()

    const currentViewMode = viewModeState.value.current.mode
    if (currentViewMode === ViewModeType.Tree)
      return useTreeView().onShrunkFolder(folder)
    else if (currentViewMode === ViewModeType.Column)
      return useColumnView().onShrunkFolder(folder)
    throw new Error("Shrinking folder supported in this mode")
  }

  /**
   * gets all the files in the current selection but sorted by the current sort.
   * TODO: should we add a "max" parameter to this, since some callers only want the first N.
   */
  getSortedFilesInCollection = (): StaticFile[] => {
    return this.sortByViewModeSort(Object.values(this.getAllFilesInCollection()))
  }

  computedSortedFilesInCollection = computed(this.getSortedFilesInCollection)

  /**
   * scrolls to the file in the collection. This might require us to also do some smart
   * loading of files and images, etc.
   */
  scrollToFileInCollection = (fileId: string) => {
    const currentViewMode = useViewMode().state.value.current.mode
    if (currentViewMode === ViewModeType.Icon)
      return useIconView().scrollToFile(fileId)
    else if (currentViewMode === ViewModeType.Grid)
      return useGridView().scrollToFile(fileId)
    else if (currentViewMode === ViewModeType.Feed)
      return useFeedView().scrollToFile(fileId)
    else if (currentViewMode === ViewModeType.Detail)
      return useDetailView().scrollToFile(fileId)
    else if (currentViewMode === ViewModeType.Table)
      return useTableView().scrollToFile(fileId)
    else if (currentViewMode === ViewModeType.Tree)
      return useTreeView().scrollToFile(fileId)
    else if (currentViewMode === ViewModeType.Column)
      return useColumnView().scrollToFile(fileId)
    else if (currentViewMode === ViewModeType.Gallery)
      return useGalleryView().scrollToFile(fileId)
    throw new Error("Selecting all not supported in this mode")
  }

  /**
   * Gives the currently active folder. This is not the current root, in the cases of
   * column view and tree view. For column and tree view it is the last opened folder
   */
  currentlyActiveFolder = () => {
    const { currentRootFolder } = useNavigation()
    const { state: viewModeState } = useViewMode()

    // column and tree view don't use the "root" folder by default.
    let lastOpenedFolderId
    const currentViewMode = viewModeState.value.current.mode
    if (currentViewMode === ViewModeType.Column)
      lastOpenedFolderId = useColumnView().state.value.lastOpenedFolderId
    else if (currentViewMode === ViewModeType.Tree)
      lastOpenedFolderId = useTreeView().state.value.lastOpenedFolderId

    if (isDefined(lastOpenedFolderId))
      return this.getStaticFile(lastOpenedFolderId)
    return currentRootFolder.value?.asStatic()
  }

  /**
   * TODO: highlight on create
   */
  createNewFolderInCollection = (parent: StaticFile) => {
    const { createFolder } = useFileSystem()

    if (!isDefined(parent) || !parent.isFolder)
      return console.error("Not in a folder")

    // find a unique name by looking inside of the parent to look for instances of
    // `New Folder (x)`

    const names = new Set<string>()
    for (const file of Object.values(this.state.value.contents))
      if (file.inferredParentId() === parent.id)
        names.add(file.name()!)

    let name = "New Folder"
    if (names.has(name)) {
      let i = 1
      while (true) {
        if (names.has(`New Folder (${i})`))
          i += 1
        else
          break
      }
      name = `New Folder (${i})`
    }

    createFolder(parent, name)
  }

  startFileRename = async (file: StaticFile) => {
    const { emitEntityEvent } = useEvents()
    const { showViewModeProperty, isShowingProperty } = useViewMode()
    if (!isShowingProperty(PropertyId.FILE_NAME)) {
      showViewModeProperty(PropertyId.FILE_NAME)
      await nextTick() // wait for the property to be shown
    }
    emitEntityEvent("requestedPropertyEdit", file.id, {
      fileId: file.id,
      propertyId: PropertyId.FILE_NAME,
      location: "collection-view",
    }, false)
  }

  /**
   * Updates the internal content bounds of items in the collection view, which we don't want
   * to do every frame, so we call it manually here. This could potentially go in a better
   * place, such as useGestures, etc.
   */
  updateFileContentBoundsSync = () => {
    const currentViewMode = useViewMode().state.value.current.mode
    if (currentViewMode === ViewModeType.Icon)
      return useIconView().updateContentBoundsSync()
    else if (currentViewMode === ViewModeType.Grid)
      return useGridView().updateContentBoundsSync()
    else if (currentViewMode === ViewModeType.Feed)
      return useFeedView().updateContentBoundsSync()
    else if (currentViewMode === ViewModeType.Detail)
      return useDetailView().updateContentBoundsSync()
    else if (currentViewMode === ViewModeType.Table)
      return useTableView().updateContentBoundsSync()
    else if (currentViewMode === ViewModeType.Tree)
      return useTreeView().updateContentBoundsSync()
    else if (currentViewMode === ViewModeType.Column)
      return useColumnView().updateContentBoundsSync()
    else if (currentViewMode === ViewModeType.Gallery)
      return useGalleryView().updateContentBoundsSync()
    throw new Error("Selecting all not supported in this mode")
  }

  getContentBounds = (): Readonly<Record<string, DOMRect>> => {
    return this.state.value.contentBounds
  }

  contentBounds = computed(this.getContentBounds)
}

const collectionView = new CollectionView("collection-view", theme.colors.amber.hex)
export default function () {
  return collectionView.use()
}
